import {
  getForecast as fetchLiveForecast,
  getStops as fetchLiveStops,
  isPlaceholderTram,
  LUAS_FORECASTS_BASE_URL,
  LuasForecast,
  LuasLine,
  LuasTram,
  parseForecastXml,
  parseStopsXml,
} from "../../api/luas-forecasts-api"

export { LUAS_FORECASTS_BASE_URL, parseForecastXml, parseStopsXml }
export type { LuasForecast, LuasLine, LuasTram }

export const LUAS_FALLBACK_STOPS_URL = "/luas-fallback/stops.json"
export const LUAS_FALLBACK_FORECASTS_URL = "/luas-fallback/forecasts.json"

type FallbackStopsPayload = {
  lines?: LuasLine[]
}

type FallbackForecastsPayload = {
  forecasts?: Record<string, LuasForecast>
}

export function lineSlug(name: string): string {
  const value = name.toLowerCase()
  if (value.includes("green")) return "green"
  if (value.includes("red")) return "red"
  return value.trim()
}

export function formatDueMins(dueMins: string): string {
  if (!dueMins) return ""
  if (dueMins.toUpperCase() === "DUE") return "DUE"
  return dueMins
}

export function isDisplayableTram(tram: LuasTram): boolean {
  return !isPlaceholderTram(tram)
}

export function readStopQuery(search = typeof window === "undefined" ? "" : window.location.search): {
  line: string
  stop: string
} {
  const params = new URLSearchParams(search)
  return {
    line: (params.get("line") || "").toLowerCase(),
    stop: (params.get("stop") || "").toUpperCase(),
  }
}

export function writeStopQuery(line: string, stop: string): void {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  if (line) url.searchParams.set("line", line)
  else url.searchParams.delete("line")
  if (stop) url.searchParams.set("stop", stop)
  else url.searchParams.delete("stop")
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`)
}

export function resolveSelection(
  lines: LuasLine[],
  lineSlugValue: string,
  stopAbrev: string
): { lineName: string; stopAbrev: string } {
  const line =
    lines.find(item => lineSlug(item.name) === lineSlugValue) ||
    lines.find(item => item.stops.some(stop => stop.abrev.toUpperCase() === stopAbrev))

  if (!line) return { lineName: "", stopAbrev: "" }

  const stop = line.stops.find(item => item.abrev.toUpperCase() === stopAbrev)
  return { lineName: line.name, stopAbrev: stop?.abrev ?? "" }
}

async function fetchOk(url: string): Promise<Response> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response
}

export async function loadStops(): Promise<LuasLine[]> {
  try {
    const lines = await fetchLiveStops()
    if (lines.length) return lines
  } catch (error) {
    console.error("[luas] Live stops API unavailable, trying fallback", error)
  }

  const fallback: FallbackStopsPayload = await (await fetchOk(LUAS_FALLBACK_STOPS_URL)).json()
  if (fallback.lines?.length) return fallback.lines
  throw new Error("Stops unavailable")
}

export async function loadForecast(stopAbrev: string): Promise<LuasForecast> {
  const stopId = stopAbrev.toUpperCase()

  try {
    const forecast = await fetchLiveForecast(stopId)
    if (forecast.stop || forecast.directions.length) return forecast
  } catch (error) {
    console.error("[luas] Live forecast API unavailable, trying fallback", error)
  }

  const fallback: FallbackForecastsPayload = await (await fetchOk(LUAS_FALLBACK_FORECASTS_URL)).json()
  const forecast = fallback.forecasts?.[stopId]
  if (forecast) return forecast
  throw new Error("Forecast unavailable")
}
